import React, { useState, useEffect } from "react";

const EditSubtaskModal = ({ isOpen, onClose, onSave, initialName, initialDescription }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [name, setName] = useState(initialName);
    const [description, setDescription] = useState(initialDescription);

    // Reset state when modal opens or props change
    useEffect(() => {
        setName(initialName);
        setDescription(initialDescription);
        setIsEditing(false); // Always start in View mode
    }, [initialName, initialDescription, isOpen]);

    const handleSave = () => {
        if (name.trim()) {
            onSave(name, description);
            setIsEditing(false); // Optional: Switch back to view mode instead of closing
            onClose();
        }
    };

    const handleCancel = () => {
        setName(initialName);
        setDescription(initialDescription);
        setIsEditing(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-[60] transition-opacity">
            <div
                className="bg-white rounded-xl w-full max-w-md mx-4 shadow-2xl overflow-hidden transform transition-all scale-100"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header Bar */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-800">
                        {isEditing ? "Edit Info" : "Subtask Info"}
                    </h3>

                    {/* Top Right Edit Toggle */}
                    {!isEditing && (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="text-sm font-medium text-purple-600 hover:text-purple-800 transition-colors"
                        >
                            Edit
                        </button>
                    )}
                </div>

                <div className="p-6">
                    {isEditing ? (
                        // ================= EDIT MODE =================
                        <div className="flex flex-col gap-5 animate-in fade-in duration-200">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                    Name
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-white transition-all font-medium text-gray-900"
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                    Description
                                </label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg h-32 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-white transition-all text-gray-700"
                                    placeholder="Add details about this subtask..."
                                />
                            </div>

                            <div className="flex justify-end space-x-3 pt-2">
                                <button
                                    onClick={handleCancel}
                                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    className="px-5 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 shadow-sm transition-all"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    ) : (
                        // ================= VIEW MODE (WhatsApp Style) =================
                        <div className="flex flex-col items-center text-center animate-in fade-in duration-200">
                            {/* Icon Placeholder (Optional, adds to the WhatsApp feel) */}
                            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                                <svg className="w-8 h-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                            </div>

                            {/* Name Display */}
                            <h2 className="text-xl font-bold text-gray-900 mb-2 leading-tight">
                                {name}
                            </h2>

                            <div className="w-full border-t border-gray-100 my-4"></div>

                            {/* Description Section */}
                            <div className="w-full text-left">
                                {!description || description.trim() === "" ? (
                                    <button
                                        onClick={() => setIsEditing(true)}
                                        className="w-full text-left group flex items-center py-2"
                                    >
                                        <span className="text-purple-600 font-medium group-hover:text-purple-700 flex items-center">
                                            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                            </svg>
                                            Add subtask description
                                        </span>
                                    </button>
                                ) : (
                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                                        <p className="text-gray-600 text-sm whitespace-pre-wrap leading-relaxed">
                                            {description}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Close Button (Bottom) */}
                            <button
                                onClick={onClose}
                                className="mt-8 text-gray-400 hover:text-gray-600 text-sm"
                            >
                                Close
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EditSubtaskModal;